<script lang="ts" setup>
import { userType } from '@/constants/UserType';
import { AppButton, AppLogo, AppPassword, AppTextField, AppTypography } from '@/core/components';
import { usePostAuthRedirect } from '@/modules/auth/composables/usePostAuthRedirect';
import { getSocialAuthErrorMessage } from '@/modules/auth/composables/useSocialAuth';
import type { CommonErrorResponse } from '@/types/ErrorResponse';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useAuthStore } from '../store/auth';
import type { LoginForm, LoginResponse } from '../types';
import SocialLoginButtons from './SocialLoginButtons.vue';

const { VALIDATIONS, PASSWORD_VALIDATION } = useValidation();
const { v$, form, handleSubmit, getErrors } = useForm<LoginForm>({
  form: {
    email: '',
    password: '',
  },
  rules: {
    email: {
      required: VALIDATIONS.required('Email'),
      email: VALIDATIONS.email(),
      maxLength: VALIDATIONS.maxLength('Email', 255),
    },
    password: PASSWORD_VALIDATION,
  },
});

const { user } = storeToRefs(useAuthStore());
const router = useRouter();
const route = useRoute();

const { token, initToken } = await useGenerateReCaptcha('login');
const loginError = ref({ data: { message: '' } });

const { completePostAuth, fetchingCarts, verifyLoading } = usePostAuthRedirect();

const {
  data,
  error: submitError,
  pending: loginLoading,
  execute: execSubmit,
} = useSubmit<{ data: LoginResponse }, CommonErrorResponse<LoginForm>>();
async function handleSignIn(): Promise<void> {
  handleSubmit(async function () {
    try {
      await execSubmit('auth/login', {
        method: 'POST',
        body: {
          username: form.value.email,
          password: form.value.password,
        },
        headers: {
          ...(token.value && { 'X-Recaptcha-Token': token.value }),
        },
      });

      if (submitError.value) {
        await initToken();
        loginError.value.data.message = submitError.value.data?.message || 'Unable to sign in';
        return;
      }

      const auth = data.value?.data;

      if (!auth) {
        await initToken();
        loginError.value.data.message = 'Unable to sign in';
        return;
      }

      if (auth.user.account_type === userType.DRIVER) {
        user.value = null;
        await router.push({ path: '/driver', query: { email: form.value.email } });
        return;
      }

      await completePostAuth({
        accessToken: auth.access_token,
        user: auth.user,
      });
    } catch (error: any) {
      await initToken();
      loginError.value.data = error.data || { message: 'Unable to sign in' };
    }
  });
}

const errors = computed(() => {
  return getErrors({ email: loginError.value?.data?.message });
});

const socialErrorMessage = computed(() => {
  const socialError = typeof route.query.social_error === 'string' ? route.query.social_error : null;

  if (!socialError) {
    return '';
  }

  return getSocialAuthErrorMessage({ error_code: socialError });
});

const sessionMessage = computed(() =>
  route.query.reason === 'email-changed'
    ? 'Your email was changed and all sessions were signed out. Sign in with your new email address.'
    : ''
);
</script>

<template>
  <v-form @submit.prevent="handleSignIn" class="auth-form">
    <ClientOnly>
      <AppLogo class="mb-12" height="72" width="72" />
      <AppTypography element="h1" type="large" class="mb-8">Sign in</AppTypography>

      <v-alert v-if="socialErrorMessage" type="error" variant="tonal" class="mb-6" data-testid="social-error">
        {{ socialErrorMessage }}
      </v-alert>

      <v-alert v-if="sessionMessage" type="success" variant="tonal" class="mb-6" data-testid="session-message">
        {{ sessionMessage }}
      </v-alert>

      <AppTextField
        v-model="form.email"
        :error-messages="errors.email"
        label="Email address"
        placeholder="example@email.com"
        :readonly="loginLoading"
        autocomplete="email"
        data-testid="email"
        autofocus
        data-cy="email"
        class="mb-3"
      >
      </AppTextField>

      <AppPassword v-model="form.password" :error-messages="errors.password" data-cy="password" class="mb-3" />

      <nuxt-link to="/reset-password" class="text-decoration-none">
        <AppTypography element="span" type="subheadline-2" class="font-weight-bold" color="primary-1">
          Forgot password
        </AppTypography>
      </nuxt-link>

      <AppButton
        block
        class="mt-8 mb-8"
        data-cy="submit"
        data-testid="submit"
        :loading="loginLoading || verifyLoading || fetchingCarts"
        :disabled="v$.$invalid"
        >Sign in</AppButton
      >

      <SocialLoginButtons class="mb-8" :disabled="loginLoading || verifyLoading || fetchingCarts" />

      <AppTypography element="span" type="headline-2" class="d-block text-center text-decoration-none">
        Don't have an account yet?
        <nuxt-link to="/register" class="font-weight-bold text-decoration-none text-primary-1" data-cy="sign-up"
          >Sign up</nuxt-link
        >
      </AppTypography>
    </ClientOnly>
  </v-form>
</template>

<style lang="scss"></style>
